import React, { useMemo } from "react"

import classNames from "classnames"
import PropTypes from "prop-types"

import { InternalExternalLink } from "../InternalExternalLink"
import { RCAImage } from "../RCAImage"
import { Tag } from "../Tag"
import * as styles from "./TagCard.module.scss"

const CardLinkWrapper = ({
  to,
  children,
  noTabIndex = false,
  className,
  ...props
}) => {
  if (!to) {
    return <span className={className}>{children}</span>
  }

  return (
    <InternalExternalLink
      className={classNames(styles.link, className)}
      to={to}
      tabIndex={noTabIndex ? -1 : 0}
      {...props}
    >
      {children}
    </InternalExternalLink>
  )
}

const TagCard = ({
  tags = [],
  tagSlot = null,
  footerSlot = null,
  explicit = false,
  title,
  subtitle,
  children,
  image,
  cloudinaryImage,
  sanityImage,
  imageTabs = [],
  alt,
  to,
  imageProps = {},
  className,
  contentClassName,
  footerClassName,
  forceImage = false,
  itemSlug,
}) => {
  const showImage = image || sanityImage || forceImage || cloudinaryImage

  const derivedImageProp = useMemo(() => {
    if (sanityImage) {
      return {
        image: sanityImage.image?.asset?.gatsbyImageData,
      }
    }

    if (cloudinaryImage) {
      return {
        cloudinaryImage: cloudinaryImage,
      }
    }

    if (image) {
      return {
        image: image,
      }
    }

    return false
  }, [image, cloudinaryImage, sanityImage])

  const clickExplicit = (e) => {
    e.currentTarget.parentNode.parentNode.style.opacity = "0"
    e.currentTarget.parentNode.parentNode.style.display = "none"
    e.currentTarget.parentNode.parentNode.xPos = -9999
  }

  return (
    <div
      className={classNames(
        styles.outer,
        {
          [styles.withFooter]: !!footerSlot,
        },
        className,
      )}
    >
      {showImage && (
        <div className={styles.imageOuter}>
          {explicit && (
            <div className={styles.isExplicit}>
              <div className={styles.isExplicitInner}>
                <div onClick={clickExplicit}>
                  <p className={styles.isExplicitWarning}>
                    Warning: This work contains mature or explicit content.
                  </p>
                  <p>Click to Enter</p>
                </div>
              </div>
            </div>
          )}
          <CardLinkWrapper className={styles.imageWrapper} to={to}>
            {imageTabs?.length > 0 && (
              <div className={styles.imageTabs}>
                {imageTabs.map((tab) => (
                  <span
                    className={classNames(styles.imageTabsItem, {
                      blink: tab.blink,
                    })}
                    key={tab.text}
                  >
                    {tab.text}
                  </span>
                ))}
              </div>
            )}
            {derivedImageProp && (
              <RCAImage
                className={classNames(styles.image)}
                alt={alt}
                {...derivedImageProp}
                {...imageProps}
              />
            )}
          </CardLinkWrapper>
        </div>
      )}
      {/* <CardLinkWrapper className={styles.title} to={to}>
        <Markdown
          unwrapDisallowed
          disallowedElements={["paragraph"]}
          source={title}
        />
      </CardLinkWrapper>

      {subtitle && (
        <CardLinkWrapper className={styles.subtitle} to={to} noTabIndex>
          <Markdown
            unwrapDisallowed
            disallowedElements={["paragraph"]}
            source={subtitle}
          />
        </CardLinkWrapper>
      )} */}

      {(tags.length || tagSlot) && (
        <div className={styles.tagsWrapper}>
          <div className={styles.tagsWrapperList}>
            {tags.map((tag) => (
              <Tag key={tag.text} to={`/tags/${tag.slug}`}>
                {tag.text}
              </Tag>
            ))}
          </div>
          {tagSlot && <div className={styles.tagsWrapperSlot}>{tagSlot}</div>}
        </div>
      )}

      {children && (
        <div className={classNames(styles.content, contentClassName)}>
          {children}
        </div>
      )}
      {footerSlot && (
        <div className={classNames(styles.footer, footerClassName)}>
          {footerSlot}
        </div>
      )}
    </div>
  )
}

TagCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,

  image: PropTypes.object,
  sanityImage: PropTypes.object,

  // Little tabs for the top left of the image
  imageTabs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
    }),
  ),

  // alt text for the image
  alt: PropTypes.string,

  // Possibility to link card elements
  to: PropTypes.string,

  // Array of shape { text: string, ...props}
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
    }),
  ),

  // A render prop to add next to the tags
  tagSlot: PropTypes.node,

  // A render prop to add at the foot
  footerSlot: PropTypes.node,
}

export { TagCard }
